import * as React from 'react';
import { AUTO_SCROLL_CONFIG } from '../config/constants';

interface UseAutoScrollOptions {
  enabled: boolean;
  listLength: number;
}

/**
 * Custom hook for auto-scrolling functionality in scoreboard lists.
 *
 * Automatically scrolls through a list of items with a smooth animation,
 * pausing at the end before looping back to the start.
 *
 * @param options - Configuration options
 * @param options.enabled - Whether auto-scroll is enabled
 * @param options.listLength - Length of the list to scroll through
 * @returns Ref to attach to the scrollable container
 *
 * @example
 * ```tsx
 * const scrollRef = useAutoScroll({
 *   enabled: autoScrollEnabled,
 *   listLength: data.length
 * });
 *
 * return <Box ref={scrollRef} overflowY="auto">...</Box>;
 * ```
 */
export const useAutoScroll = <T extends HTMLElement = HTMLDivElement>({
  enabled,
  listLength,
}: UseAutoScrollOptions): React.RefObject<T> => {
  const scrollRef = React.useRef<T>(null);

  React.useEffect((): (() => void) => {
    let animationId: number | null = null;
    let pauseTimer: ReturnType<typeof setTimeout> | null = null;
    let isPaused = false;
    let isRunning = false;

    // Early return if auto-scroll is disabled
    if (!enabled) {
      return (): void => {
        if (animationId) cancelAnimationFrame(animationId);
        if (pauseTimer) clearTimeout(pauseTimer);
      };
    }

    const container = scrollRef.current;
    if (!container) {
      return (): void => {
        if (animationId) cancelAnimationFrame(animationId);
        if (pauseTimer) clearTimeout(pauseTimer);
      };
    }

    // Reset scroll position and start
    container.scrollTop = 0;
    isRunning = true;

    const scroll = (): void => {
      if (!isRunning || !container) return;

      if (isPaused) {
        animationId = requestAnimationFrame(scroll);
        return;
      }

      const maxScroll = container.scrollHeight - container.clientHeight;

      // Only scroll if there's overflow
      if (maxScroll <= 0) {
        animationId = requestAnimationFrame(scroll);
        return;
      }

      container.scrollTop += AUTO_SCROLL_CONFIG.SCROLL_SPEED_PX;

      // When reaching the end, pause then loop back to start
      if (container.scrollTop >= maxScroll) {
        isPaused = true;
        pauseTimer = setTimeout((): void => {
          if (!isRunning) return;
          container.scrollTop = 0;
          isPaused = false;
        }, AUTO_SCROLL_CONFIG.PAUSE_DURATION_MS);
      }

      animationId = requestAnimationFrame(scroll);
    };

    // Small delay to let the DOM settle and ref to be attached
    const startTimer = setTimeout((): void => {
      if (isRunning) {
        animationId = requestAnimationFrame(scroll);
      }
    }, AUTO_SCROLL_CONFIG.START_DELAY_MS);

    return (): void => {
      isRunning = false;
      clearTimeout(startTimer);
      if (pauseTimer) {
        clearTimeout(pauseTimer);
        pauseTimer = null;
      }
      if (animationId) {
        cancelAnimationFrame(animationId);
        animationId = null;
      }
    };
  }, [enabled, listLength]);

  return scrollRef;
};
